import { AllTypes, KeyType } from "generated/graphql.cache";
import { KeyingConfig } from "@urql/exchange-graphcache";
import {
  CertificateResponse,
  DeviceBasicData,
  License,
  Ns_Ls_Entitlement,
  Ns_Ls_EntitlementGroupings,
  Ns_Ls_EntitlementGroupingSet,
  Ns_Ls_Instance,
  Ns_Ls_ProductLatestVersion,
  Ns_Ls_ProductVersions,
  Ns_Ls_SoftwareCatalog,
  Policy,
  ProvisioningAccount,
  ResourceFileDto,
  SettingResponse,
  User
} from "generated/graphql.schema";

const nullKey = () => null;
/**
 * Define data that has no real key, will be cached by the parent type .
 */
const nullKeys: Partial<KeyType> = {
  Aggregate: nullKey,
  AverageIndicator: nullKey,
  ClaritiTrialUsageStats: nullKey,
  ClaritiTrialUserInfo: nullKey,
  CountOfTotalIndicator: nullKey,
  CountAverageIndicator: nullKey,
  EntityConnection: nullKey,
  EntityConnectionInfo: nullKey,
  KeyBucket: nullKey,
  FieldMissingBucket: nullKey,
  OSState: nullKey,
  SoftwareUpdateStatus: nullKey,
  Address: nullKey,
  Role: nullKey,
  NormalizedDeviceSetting: nullKey,
  Permission: nullKey,
  PolyOSConfig: nullKey,
  PolyOSConfig_content: nullKey,
  PolyOSConfig_content_conference: nullKey,
  PolyOSConfig_device: nullKey,
  PolyOSConfig_device_local: nullKey,
  PolyOSConfig_device_local_autoDaylightSavings: nullKey,
  PolyOSConfig_device_local_datetime: nullKey,
  PolyOSConfig_device_local_datetime_date: nullKey,
  PolyOSConfig_device_local_datetime_time: nullKey,
  PolyOSConfig_device_local_ntpServer: nullKey,
  PolyOSConfig_exchange: nullKey,
  PolyOSConfig_exchange_auth: nullKey,
  PolyOSConfig_exchange_meeting: nullKey,
  PolyOSConfig_exchange_meeting_reminderSound: nullKey,
  PolyOSConfig_exchange_server: nullKey,
  PolyOSConfig_homeScreen: nullKey,
  PolyOSConfig_qos: nullKey,
  PolyOSConfig_qos_diffServ: nullKey,
  PolyOSConfig_qos_dynamicBandwidth: nullKey,
  PolyOSConfig_qos_intServ: nullKey,
  PolyOSConfig_qos_LPR: nullKey,
  PolyOSConfig_qos_rsvp: nullKey,
  PolyOSConfig_upgrade: nullKey,
  PolyOSConfig_upgrade_auto: nullKey,
  PolyOSConfig_upgrade_auto_polling: nullKey,
  PolyOSConfig_upgrade_auto_server: nullKey,
  PolyOSConfig_upgrade_auto_timeFrame: nullKey,
  PolyOSConfig_video: nullKey,
  PolyOSConfig_video_monitor: nullKey,
  PolyOSConfig_video_monitor_1: nullKey,
  PolyOSConfig_video_monitor_2: nullKey,
  PolyOSConfig_voIpProt: nullKey,
  PolyOSConfig_voIpProt_H323: nullKey,
  PolyOSConfig_voIpProt_H323_gk: nullKey,
  PolyOSConfig_voIpProt_H323_gk_auth: nullKey,
  PolyOSConfig_voIpProt_SIP: nullKey,
  PolyOSConfig_voIpProt_SIP_auth: nullKey,
  TopicIndicatorDetail: nullKey,
  Point: nullKey,
  Series: nullKey,
  InsightSeriesMeta: nullKey,
  Chart: nullKey,
  InsightChartMeta: nullKey,
  TenantManagementConfiguration: nullKey,
  ConfigurationAttribute: nullKey,
  DeviceParameterDTO: nullKey,
  ExtendedDeviceParameterDTO: nullKey,
  EnumVal: nullKey,
  ParamArrayItem: nullKey,
  Param2DArrayItem: nullKey,
  ExtendedConfigParamsResponse: nullKey,
  ExtendedConfigParam: nullKey,
  SoftwareSupportUpdatePath: nullKey,
  EpUpdateImage: nullKey,
  Grant: nullKey,
  Accessor: nullKey,
  NcTeamsQueries: nullKey,
  NotificationCenter: nullKey,
  GroupAPIResponse: nullKey,
  GroupDtos: nullKey,
  FirmwareUpdateInfo: nullKey,
  Identity: nullKey,
  UserMetaDataInfo: nullKey,
  UserMetadata: nullKey,

  Constraints: nullKey,
  Capabilities: nullKey,
  StringValue: nullKey,
  BooleanValue: nullKey,
  NumberValue: nullKey,

  com: nullKey,
  poly: nullKey,
  Audio: nullKey,
  CallControl: nullKey,
  Video: nullKey,
  Wireless: nullKey,
  General: nullKey,
  DeviceOutput: nullKey,
  C10NetworkInformation: nullKey,
  C10NetworkInformationInterfaces: nullKey,
  C10NetworkInformationValue: nullKey,
  C10OutputServiceInformation: nullKey,
  SubnetRuleValidityResponse: nullKey,

  LicenseAggregations: nullKey,
  UserResponse: nullKey,
  ns_ls_EntitlementGroupingSetConnection: nullKey,
  ns_ls_EntitlementGroupingAssignmentSet: nullKey,
  ns_ls_ProductList: nullKey,
  ns_ls_ProductLatestVersionList: nullKey,
  ns_ls_SoftwareEntry: nullKey,
  Capability: nullKey,

  ImportResponse: nullKey,
  DeviceImportResponse: nullKey,
  GlobalConfigResponse: nullKey,
  GroupConfigResponse: nullKey,
  SiteConfigResponse: nullKey,
  SiteTopologyResponse: nullKey,
  ModelConfigSettingsResponse: nullKey,
  ProductBuild: nullKey,

  SiteConnectionObject: nullKey,
  RoomConnectionObject: nullKey,
  Bandwidth: nullKey
};
/**
 * Has an id field other than id or _id
 */
const customKeys: Partial<Record<AllTypes, any>> = {
  CertificateResponse: (data: Partial<CertificateResponse>) =>
    data.deviceId ?? null,
  DeviceBasicData: (data: Partial<DeviceBasicData>) => data.macAddress ?? null,
  License: (data: Partial<License>) => data.licenseKey ?? null,
  ns_ls_ClientAuthToken: (data: Partial<Ns_Ls_Entitlement>) =>
    data.recordId ?? null,
  ns_ls_Entitlement: (data: Partial<Ns_Ls_Entitlement>) =>
    data.recordId ?? null,
  ns_ls_EntitlementGroupings: (data: Partial<Ns_Ls_EntitlementGroupings>) =>
    data.tenantId ?? null,
  ns_ls_EntitlementGroupingSet: (data: Partial<Ns_Ls_EntitlementGroupingSet>) =>
    data.groupingId ?? null,
  ns_ls_Instance: (data: Partial<Ns_Ls_Instance>) => data.recordId ?? null,
  ns_ls_ProductLatestVersion: (data: Partial<Ns_Ls_ProductLatestVersion>) =>
    data.productId ?? null,
  ns_ls_ProductVersions: (data: Partial<Ns_Ls_ProductVersions>) =>
    data.productId ?? null,
  ns_ls_SoftwareCatalog: (data: Partial<Ns_Ls_SoftwareCatalog>) =>
    data.productId && data.version ? `${data.productId}/${data.version}` : null,
  Policy: (data: Partial<Policy>) => data.policyId ?? null,
  ProvisioningAccount: (data: Partial<ProvisioningAccount>) =>
    data.uuid ?? null,
  User: (data: Partial<User>) => data.user_id ?? null,
  ResourceFileDTO: (data: Partial<ResourceFileDto>) =>
    data.tenantId && data.fileName ? `${data.tenantId}/${data.fileName}` : null,
  SettingResponse: (data: Partial<SettingResponse>) => data.id ?? null
};

export default {
  ...nullKeys,
  ...customKeys
} as KeyingConfig;
